import { useEffect, useState, useMemo } from 'react'
import { reservationService, Reservation, ReservationFilters } from '@/services/reservationService'
import useAppConfigStore from '@/stores/appConfigStore'
import { authService } from '@/services/authService'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, ChevronLeft, ChevronRight, Clock, User, Store, Package, Tag, Banknote, CalendarClock, RefreshCw, Filter } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface ScheduledOrder extends Reservation {
  scheduled_at: string
}

const ScheduledOrdersPage = () => {
  const [reservations, setReservations] = useState<ScheduledOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0])
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const { systemConfig, initializeAppConfig, loading: configLoading } = useAppConfigStore()
  const navigate = useNavigate()

  const loadScheduledOrders = async () => {
    try {
      setLoading(true)
      setError(null)

      const isAdmin = authService.isAdmin()
      const merchantContext = authService.getMerchantContext()

      const filters: ReservationFilters = {}
      if (!isAdmin && merchantContext?.merchantId) {
        filters.merchant_id = merchantContext.merchantId
      }

      const response = await reservationService.getAll(filters)
      if (response.success) {
        // Filter only reservations with scheduled_at
        const scheduled = response.data.filter(
          (r): r is ScheduledOrder => r.scheduled_at != null
        )
        setReservations(scheduled)
      } else {
        setError('Échec du chargement des commandes planifiées')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadScheduledOrders()
  }, [])

  useEffect(() => {
    if (!systemConfig && !configLoading) {
      initializeAppConfig()
    }
  }, [systemConfig, configLoading, initializeAppConfig])

  const formatCurrency = (amount: number | string) => {
    const currencyCode = systemConfig?.default_currency || 'MAD'
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode
    }).format(numericAmount)
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDateLabel = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) return "Aujourd'hui"
    if (date.toDateString() === tomorrow.toDateString()) return 'Demain'

    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    })
  }

  const getStatusBadgeVariant = (status: Reservation['status']) => {
    switch (status) {
      case 'pending': return 'secondary'
      case 'confirmed': return 'default'
      case 'cancelled': return 'destructive'
      case 'completed': return 'outline'
      default: return 'default'
    }
  }

  const getStatusLabel = (status: Reservation['status']) => {
    switch (status) {
      case 'pending': return 'En attente'
      case 'confirmed': return 'Confirmée'
      case 'cancelled': return 'Annulée'
      case 'completed': return 'Terminée'
      default: return status
    }
  }

  const getUserName = (reservation: Reservation) => {
    if (reservation.user_first_name && reservation.user_last_name) {
      return `${reservation.user_first_name} ${reservation.user_last_name}`
    }
    return `User ${reservation.user_id.substring(0, 8)}...`
  }

  const getItemType = (reservation: Reservation) => {
    if (reservation.basket_id) return 'Panier'
    if (reservation.product_id) return 'Produit'
    if (reservation.offer_id) return 'Offre'
    return 'Inconnu'
  }

  // Group reservations by date
  const groupedByDate = useMemo(() => {
    const groups: Record<string, ScheduledOrder[]> = {}

    reservations.forEach(reservation => {
      const date = new Date(reservation.scheduled_at).toISOString().split('T')[0]
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(reservation)
    })

    // Sort dates
    const sorted = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))

    // Apply status filter
    if (statusFilter !== 'all') {
      return sorted
        .map(([date, orders]) => [
          date,
          orders.filter(o => o.status === statusFilter)
        ] as [string, ScheduledOrder[]])
        .filter(([, orders]) => orders.length > 0)
    }

    return sorted
  }, [reservations, statusFilter])

  // Get unique dates for navigation
  const availableDates = useMemo(() => {
    return Object.keys(
      reservations.reduce((acc, r) => {
        const date = new Date(r.scheduled_at).toISOString().split('T')[0]
        acc[date] = true
        return acc
      }, {} as Record<string, boolean>)
    ).sort()
  }, [reservations])

  const currentDateIndex = availableDates.indexOf(selectedDate)

  const goToPreviousDate = () => {
    if (currentDateIndex > 0) {
      setSelectedDate(availableDates[currentDateIndex - 1])
    }
  }

  const goToNextDate = () => {
    if (currentDateIndex < availableDates.length - 1) {
      setSelectedDate(availableDates[currentDateIndex + 1])
    }
  }

  // Filter orders for selected date
  const ordersForSelectedDate = useMemo(() => {
    return (groupedByDate.find(([date]) => date === selectedDate)?.[1] || [])
      .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
  }, [groupedByDate, selectedDate])

  // Statistics
  const totalScheduled = reservations.length
  const totalRevenue = reservations.reduce((sum, r) => {
    const amount = typeof r.total_amount === 'string' ? parseFloat(r.total_amount) : r.total_amount
    return sum + amount
  }, 0)
  const todayCount = reservations.filter(r => {
    const today = new Date().toISOString().split('T')[0]
    return new Date(r.scheduled_at).toISOString().split('T')[0] === today
  }).length
  const upcomingCount = reservations.filter(r => {
    const today = new Date().toISOString().split('T')[0]
    return new Date(r.scheduled_at).toISOString().split('T')[0] > today
  }).length

  const isAdmin = authService.isAdmin()
  const merchantContext = authService.getMerchantContext()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">Commandes planifiées</h1>
            {!isAdmin && merchantContext && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                {merchantContext.merchantName}
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">
            Visualisez et gérez les commandes programmées
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadScheduledOrders} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-purple-500" />
              Total planifiées
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalScheduled}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Banknote className="h-4 w-4 text-green-500" />
              Revenu estimé
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalRevenue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-500" />
              Aujourd'hui
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{todayCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4 text-orange-500" />
              À venir
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{upcomingCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Status Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-gray-500" />
        {['all', 'pending', 'confirmed', 'completed', 'cancelled'].map(status => (
          <Button
            key={status}
            variant={statusFilter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(status)}
          >
            {status === 'all' ? 'Tous' : getStatusLabel(status as Reservation['status'])}
          </Button>
        ))}
      </div>

      {/* Date Navigation */}
      {availableDates.length > 0 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={goToPreviousDate}
            disabled={currentDateIndex <= 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Jour précédent
          </Button>

          <div className="text-center">
            <div className="text-lg font-semibold capitalize">
              {formatDateLabel(selectedDate)}
            </div>
            <div className="text-sm text-muted-foreground">
              {new Date(selectedDate).toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {ordersForSelectedDate.length} commande{ordersForSelectedDate.length !== 1 ? 's' : ''} planifiée{ordersForSelectedDate.length !== 1 ? 's' : ''}
            </div>
          </div>

          <Button
            variant="outline"
            onClick={goToNextDate}
            disabled={currentDateIndex >= availableDates.length - 1}
          >
            Jour suivant
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      )}

      {/* Date Quick Select */}
      {availableDates.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {availableDates.map(date => (
            <Button
              key={date}
              variant={selectedDate === date ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedDate(date)}
              className="whitespace-nowrap"
            >
              {new Date(date).toLocaleDateString('fr-FR', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              })}
            </Button>
          ))}
        </div>
      )}

      {/* Orders for Selected Date */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-purple-500" />
            Commandes du {new Date(selectedDate).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-md">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : availableDates.length === 0 ? (
            <div className="text-center py-12">
              <CalendarClock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Aucune commande planifiée</p>
              <p className="text-sm text-gray-400 mt-1">
                Les commandes programmées apparaîtront ici
              </p>
            </div>
          ) : ordersForSelectedDate.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">Aucune commande pour cette date</p>
            </div>
          ) : (
            <div className="space-y-4">
              {ordersForSelectedDate.map(order => (
                <div
                  key={order.reservation_id}
                  className="flex items-center justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow cursor-pointer"
                  onClick={() => navigate(`/reservations/${order.reservation_id}`)}
                >
                  <div className="flex items-start gap-4 flex-1">
                    {/* Time */}
                    <div className="flex flex-col items-center min-w-[60px]">
                      <Clock className="h-4 w-4 text-purple-500 mb-1" />
                      <span className="text-sm font-semibold text-purple-700">
                        {formatTime(order.scheduled_at)}
                      </span>
                    </div>

                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant={getStatusBadgeVariant(order.status)}>
                          {getStatusLabel(order.status)}
                        </Badge>
                        <span className="text-xs font-mono text-gray-400">
                          #{order.reservation_id.substring(0, 8)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <User className="h-3.5 w-3.5" />
                          {getUserName(order)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Store className="h-3.5 w-3.5" />
                          {order.merchant_name || 'N/D'}
                        </span>
                        <span className="flex items-center gap-1">
                          {order.product_id ? (
                            <Package className="h-3.5 w-3.5 text-blue-500" />
                          ) : order.offer_id ? (
                            <Tag className="h-3.5 w-3.5 text-green-500" />
                          ) : (
                            <Package className="h-3.5 w-3.5 text-gray-500" />
                          )}
                          {getItemType(order)}
                        </span>
                      </div>
                    </div>

                    {/* Amount & Quantity */}
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">
                        {formatCurrency(order.total_amount)}
                      </div>
                      <div className="text-sm text-gray-500">
                        x{order.quantity}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* All Scheduled Orders Timeline */}
      {groupedByDate.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-500" />
              Calendrier des planifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {groupedByDate.map(([date, orders]) => {
                const totalForDay = orders.reduce((sum, o) => {
                  const amount = typeof o.total_amount === 'string' ? parseFloat(o.total_amount) : o.total_amount
                  return sum + amount
                }, 0)
                const isToday = date === new Date().toISOString().split('T')[0]

                return (
                  <div
                    key={date}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      isToday ? 'bg-purple-50 border border-purple-200' : 'hover:bg-gray-50'
                    } ${selectedDate === date ? 'ring-2 ring-purple-300' : ''}`}
                    onClick={() => setSelectedDate(date)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${isToday ? 'bg-purple-500' : 'bg-gray-300'}`} />
                      <div>
                        <div className="font-medium capitalize">
                          {formatDateLabel(date)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {new Date(date).toLocaleDateString('fr-FR', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="font-semibold">{orders.length} commande{orders.length !== 1 ? 's' : ''}</div>
                        <div className="text-sm text-muted-foreground">{formatCurrency(totalForDay)}</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ScheduledOrdersPage
